import graphql from 'babel-plugin-relay/macro';
import { Body, Footer, Header } from 'components/layout';
import { RouteTypeBorder } from 'components/organization/changeOrganization/RouteTypeBorder';
import compose from 'lodash.flowright';
import PropType from 'prop-types';
import React from 'react';
import injectSheet from 'react-jss';
import { createFragmentContainer } from 'react-relay';
import { matchPath, Route, withRouter } from 'react-router-dom';

import { ROUTE_GENERATE_KEYS, ROUTE_ORG } from '../../../constant/routes';
import styles from './signedInViewer.style';

export const UserContext = React.createContext(() => {});

export const withUser = Component => props => (
  <UserContext.Consumer>
    {({ user, selectedOrganization, superUserOrgs, switchOrganization }) => (
      <Component
        selectedOrganization={selectedOrganization}
        superUserOrgs={superUserOrgs}
        switchOrganization={switchOrganization}
        user={user}
        {...props}
      />
    )}
  </UserContext.Consumer>
);

export const SELECTED_ORG_KEY = 'SELECTED_ORG';

// const loadSelectedOrgId = () => localStorage.getItem(SELECTED_ORG_KEY);

const saveSelectedOrgId = orgId => {
  localStorage.setItem(SELECTED_ORG_KEY, orgId);
};

// Organizations the current user can view: their member orgs plus, for
// superusers and developers, every org returned in `superUserOrgs`. Such a user
// can open an org they are not a member of (e.g. a demo org), which only appears
// in `superUserOrgs`. Resolving against BOTH lists prevents silently falling
// back to the user's first member org — which made the sidebar nav links point
// at the wrong organization when viewing such an org.
const findViewableOrg = ({ user, superUserOrgs }, orgId) => {
  const orgs = [
    ...(user.organizations?.nodes ?? []),
    ...(superUserOrgs?.nodes ?? [])
  ];
  // `rowId` is a BigInt scalar serialized as a string; coerce both sides so a
  // number/string mismatch can never cause a miss.
  return orgs.find(org => String(org.rowId) === String(orgId));
};

const resolveSelectedOrganization = props => {
  const { user, location } = props;
  const [firstOrg] = user.organizations.nodes;

  const match = matchPath(location.pathname, {
    path: '/organization/:orgId',
    exact: false,
    strict: false
  });
  const organizationId = match?.params.orgId;

  // No org in the URL (e.g. profile pages): keep the user's first member org.
  if (!organizationId) {
    return firstOrg;
  }

  return findViewableOrg(props, organizationId) ?? firstOrg;
};

export class SignedInViewer extends React.Component {
  static propTypes = {
    classes: PropType.object.isRequired,
    user: PropType.object.isRequired,
    component: PropType.func.isRequired,
    history: PropType.object.isRequired,
    location: PropType.object.isRequired,
    superUserOrgs: PropType.object,
    withoutHeader: PropType.bool
  };

  static defaultProps = {
    withoutHeader: false,
    superUserOrgs: null
  };

  constructor(props) {
    super(props);

    const selectedOrganization = resolveSelectedOrganization(props);

    if (selectedOrganization) saveSelectedOrgId(selectedOrganization.rowId);

    this.state = {
      selectedOrganization
    };
  }

  componentDidMount() {
    this.update();
  }

  componentDidUpdate(prevProps) {
    this.update(prevProps);
  }

  update = prevProps => {
    const {
      location,
      history,
      user: {
        account: { signingKey },
        name,
        email,
        rowId
      }
    } = this.props;

    // Set user context if first load (no prevProps) or any of the context values
    // have changed.
    if (
      prevProps === undefined ||
      name !== prevProps.user.name ||
      email !== prevProps.user.email ||
      rowId !== prevProps.user.rowId
    ) {
      // apm.setUserContext({
      //   id: rowId,
      //   username: name,
      //   email
      // });
    }

    // If the user is missing a key this means that they need to generate them,
    // so redirect to key generation page. This should only happen when a user
    // sign up is interupted.
    if (!signingKey && location.pathname !== ROUTE_GENERATE_KEYS) {
      history.push(ROUTE_GENERATE_KEYS);
    }

    // Keep the selected organization aligned with the URL so navigation that
    // bypasses `switchOrganization` (browser back/forward, cross-org links)
    // never leaves the app pointing at a stale organization.
    const urlOrgId = matchPath(location.pathname, {
      path: '/organization/:orgId',
      exact: false,
      strict: false
    })?.params.orgId;
    if (urlOrgId) {
      const resolved = findViewableOrg(this.props, urlOrgId);
      if (
        resolved &&
        resolved.rowId !== this.state.selectedOrganization?.rowId
      ) {
        saveSelectedOrgId(resolved.rowId);
        this.setState({ selectedOrganization: resolved });
      }
    }
  };

  switchOrganization = org => {
    const { history, location } = this.props;
    const path = location.pathname;

    this.setState({ selectedOrganization: org });
    const menuLocation = path.split('/')[3] || 'members';
    const newPath = `${ROUTE_ORG}/${org.rowId}/${menuLocation}`;
    history.push(newPath);
  };

  render() {
    const {
      classes,
      user,
      superUserOrgs,
      withoutHeader,
      component: Component,
      ...props
    } = this.props;
    const { selectedOrganization } = this.state;

    return (
      <UserContext.Provider
        value={{
          user,
          selectedOrganization,
          superUserOrgs,
          switchOrganization: this.switchOrganization
        }}
      >
        <RouteTypeBorder
          location={this.props.location}
          superUserOrgs={superUserOrgs}
          user={user}
        />
        <Route
          {...props}
          render={p => {
            if (withoutHeader) return <Component {...p} />;
            return (
              <div className={classes.root}>
                <div className={classes.header}>
                  <Header user={user} />
                </div>
                <div className={classes.body}>
                  <Body user={user}>
                    <Component {...p} />
                  </Body>
                  <Footer />
                </div>
              </div>
            );
          }}
        />
      </UserContext.Provider>
    );
  }
}

export default createFragmentContainer(
  compose(withRouter, injectSheet(styles))(SignedInViewer),
  {
    superUserOrgs: graphql`
      fragment signedInViewer_superUserOrgs on OrganizationsConnection {
        nodes {
          id
          rowId
          name
          avatar
          organizationType
          primaryOrganization {
            rowId
            name
            organizationType
          }
        }
      }
    `,
    user: graphql`
      fragment signedInViewer_user on User {
        rowId
        firstName
        lastName
        name
        email
        phone
        avatar
        canUpdate
        isSuperuser
        isDeveloper
        account {
          id
          rowId
          encryptionKey {
            rowId
            publicKey
          }
          signingKey {
            rowId
            publicKey
            privateKey {
              decrypted
              passwordProtected
            }
          }
        }
        organizations {
          nodes {
            id
            rowId
            name
            avatar
            canUpdate
            organizationType
            primaryOrganization {
              rowId
              name
              organizationType
            }
          }
        }
        teams {
          totalCount
          nodes {
            id
            rowId
            name
            avatar
            canUpdate
            organization {
              id
              rowId
              name
              avatar
            }
          }
        }
      }
    `
  }
);
